/**
 * InvoicesList — Factures émises (style BE-TAYE exact).
 * Header simple + filtres + table dense + Sheet latéral pour détail.
 * KPI/graphiques sont dans /app/analyse-ca (séparation BE-TAYE).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Search, Download, FileText, Paperclip, X, Plus, ChevronLeft, ChevronRight,
  Trash2, CheckCircle2, Filter, Calendar, Sparkles, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import InvoiceDetailSheet from "@/components/InvoiceDetailSheet";
import { useAuth } from "@/context/AuthContext";
import { Lock } from "lucide-react";

const fmtCurrency = (v) => new Intl.NumberFormat("fr-FR", {
  style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(v) || 0);

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("fr-FR"); } catch { return "—"; }
};

// Sync a param to URL: undefined/"" removes it; string values set it.
const setUrlParam = (params, key, value) => {
  if (value === undefined || value === null || value === "" || value === "all") {
    params.delete(key);
  } else {
    params.set(key, String(value));
  }
};

export default function InvoicesList({ embedded = false } = {}) {
  const { hasPermission, hasFeature } = useAuth();
  const canWrite = hasPermission("invoices.write");
  const canRebill = hasFeature("rebilling");
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalTtc, setTotalTtc] = useState(0);
  const [totalHt, setTotalHt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [reconFilter, setReconFilter] = useState(searchParams.get("recon") || "all");
  const [monthFilter, setMonthFilter] = useState(searchParams.get("month") || "all");   // "01"…"12"
  const [yearFilter, setYearFilter] = useState(searchParams.get("year") || "all");     // "YYYY"
  const [dateFrom, setDateFrom] = useState(searchParams.get("from") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("to") || "");
  const [amountMin, setAmountMin] = useState(searchParams.get("min") || "");
  const [amountMax, setAmountMax] = useState(searchParams.get("max") || "");
  const [showAdvanced, setShowAdvanced] = useState(!!(searchParams.get("from") || searchParams.get("to") || searchParams.get("min") || searchParams.get("max")));
  const [selectedId, setSelectedId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "0", 10));
  const [selection, setSelection] = useState(new Set());
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [markPaidYear, setMarkPaidYear] = useState(new Date().getFullYear() - 1);
  const [busy, setBusy] = useState(false);
  // Pastille suggestion — map { invoice_id: { tx_id, tx_date, tx_amount, ...} }
  const [suggestions, setSuggestions] = useState({});
  const [suggestionDialog, setSuggestionDialog] = useState(null); // { invoice, suggestion }
  const [reconciling, setReconciling] = useState(false);
  const PAGE_SIZE = 50;

  const MONTHS_FR = useMemo(() => [
    { value: "01", label: "Janvier" }, { value: "02", label: "Février" },
    { value: "03", label: "Mars" }, { value: "04", label: "Avril" },
    { value: "05", label: "Mai" }, { value: "06", label: "Juin" },
    { value: "07", label: "Juillet" }, { value: "08", label: "Août" },
    { value: "09", label: "Septembre" }, { value: "10", label: "Octobre" },
    { value: "11", label: "Novembre" }, { value: "12", label: "Décembre" },
  ], []);
  const YEARS = useMemo(() => {
    const cur = new Date().getFullYear();
    return Array.from({ length: 8 }, (_, i) => String(cur - i));
  }, []);

  // Debounce the search input into `debouncedSearch` and reset to page 0 —
  // this replaces the previous 2-effects setup that fired duplicate fetches
  // on mount and on every search commit.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Build the query month string :
  //  - Si mois + année → "YYYY-MM"
  //  - Si mois seul → "MM" (backend filtre sur ce mois pour toutes les années)
  //  - Sinon → null (bascule sur `year` s'il est renseigné)
  const monthQuery = useMemo(() => {
    if (monthFilter !== "all" && yearFilter !== "all") return `${yearFilter}-${monthFilter}`;
    if (monthFilter !== "all") return monthFilter;
    return null;
  }, [monthFilter, yearFilter]);

  // Sync active filters to URL so a user can bookmark / share a filtered view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    setUrlParam(next, "q", debouncedSearch);
    setUrlParam(next, "status", statusFilter);
    setUrlParam(next, "recon", reconFilter);
    setUrlParam(next, "month", monthFilter);
    setUrlParam(next, "year", yearFilter);
    setUrlParam(next, "from", dateFrom);
    setUrlParam(next, "to", dateTo);
    setUrlParam(next, "min", amountMin);
    setUrlParam(next, "max", amountMax);
    setUrlParam(next, "page", page > 0 ? page : "");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter, reconFilter, monthFilter, yearFilter, dateFrom, dateTo, amountMin, amountMax, page]);

  // Single source of truth for the fetch — refetch when any server-side filter changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
        if (statusFilter !== "all") params.status = statusFilter;
        if (reconFilter !== "all") params.reconciliation = reconFilter;
        if (debouncedSearch) params.search = debouncedSearch;
        if (monthQuery) params.month = monthQuery;
        else if (yearFilter !== "all") params.year = yearFilter;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (amountMin !== "" && !Number.isNaN(Number(amountMin))) params.amount_min = Number(amountMin);
        if (amountMax !== "" && !Number.isNaN(Number(amountMax))) params.amount_max = Number(amountMax);
        const { data } = await api.get("/invoices", { params });
        if (cancelled) return;
        setInvoices(data.invoices || []);
        setTotal(data.total || (data.invoices || []).length);
        setTotalTtc(Number(data.total_ttc) || 0);
        setTotalHt(Number(data.total_ht) || 0);
      } catch (e) {
        if (!cancelled) toast.error(apiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, statusFilter, reconFilter, debouncedSearch, monthQuery, yearFilter, dateFrom, dateTo, amountMin, amountMax]);

  const clearFilters = () => {
    setSearch(""); setDebouncedSearch("");
    setStatusFilter("all"); setReconFilter("all");
    setMonthFilter("all"); setYearFilter("all");
    setDateFrom(""); setDateTo("");
    setAmountMin(""); setAmountMax("");
    setShowAdvanced(false);
    setPage(0);
  };
  const hasFilters = search || statusFilter !== "all" || reconFilter !== "all"
    || monthFilter !== "all" || yearFilter !== "all" || dateFrom || dateTo
    || amountMin !== "" || amountMax !== "";
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canPrev = page > 0;
  const canNext = page < pageCount - 1;

  // Fetch reconciliation suggestions in background (once per session load).
  // Non-blocking — pastille apparaît quand la promesse résout.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/invoices/reconciliation-suggestions");
        if (!cancelled) setSuggestions(r.data.suggestions || {});
      } catch { /* silent — feature dégrade proprement */ }
    })();
    return () => { cancelled = true; };
  }, [invoices.length]);  // recharge après reload liste

  const confirmReconciliation = async () => {
    if (!suggestionDialog) return;
    const { invoice, suggestion } = suggestionDialog;
    setReconciling(true);
    try {
      // Match complet : allocation = montant tx entier sur cette facture.
      // Si la facture est plus petite, backend renverra 400 amount_exceeds_remaining.
      const totalTtc = invoice.totals?.total_ttc ?? invoice.total_ttc ?? 0;
      const paid = Number(invoice.paid_amount_ttc || 0);
      const credited = Number(invoice.credited_amount_ttc || 0);
      const remaining = Math.max(0, totalTtc - paid - credited);
      const allocAmount = Math.min(Number(suggestion.tx_amount), remaining);
      await api.post(
        `/reconciliation/transactions/${suggestion.tx_id}/match-allocations`,
        {
          invoice_kind: "customer",
          allocations: [{ invoice_id: invoice.id, amount: allocAmount }],
        });
      toast.success(`✓ Facture ${invoice.number || invoice.invoice_number} rapprochée`);
      // Retire la suggestion + refresh la liste
      setSuggestions((prev) => {
        const next = { ...prev };
        delete next[invoice.id];
        return next;
      });
      setSuggestionDialog(null);
      // Force refetch by bumping the debouncedSearch state to same value (triggers useEffect)
      setPage((p) => p);
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (statusFilter !== "all") params.status = statusFilter;
      if (reconFilter !== "all") params.reconciliation = reconFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      if (monthQuery) params.month = monthQuery;
      else if (yearFilter !== "all") params.year = yearFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/invoices", { params });
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
      setTotalTtc(Number(data.total_ttc) || 0);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || apiError(e));
    } finally { setReconciling(false); }
  };

  const exportExcel = () => {
    const rows = invoices.map((i) => ({
      Numéro: i.number || i.invoice_number || "",
      Client: i.client?.name || "",
      Date: i.issued_at || i.invoice_date || "",
      Échéance: i.due_date || "",
      "Total HT": i.totals?.subtotal_ht ?? i.total_ht ?? 0,
      "Total TVA": i.totals?.total_vat ?? i.total_vat ?? 0,
      "Total TTC": i.totals?.total_ttc ?? i.total_ttc ?? 0,
      Statut: i.status,
      Source: i.imported_from?.split(":")[0] || "native",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Factures");
    XLSX.writeFile(wb, `factures_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`${rows.length} factures exportées`);
  };

  const openDetail = (inv) => { setSelectedId(inv.id); setSheetOpen(true); };

  const toggleRow = (id, checked) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const draftIdsOnPage = invoices.filter((i) => i.status === "draft").map((i) => i.id);
  const toggleAll = (checked) => {
    setSelection(checked ? new Set(draftIdsOnPage) : new Set());
  };

  const runBatchDelete = async () => {
    if (!selection.size) return;
    if (!window.confirm(`Supprimer ${selection.size} brouillon(s) ? Action irréversible.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/invoices/actions/batch-delete",
        { ids: Array.from(selection) });
      const { success, failed, not_found } = r.data;
      toast.success(`${success.length} supprimée(s), ${failed.length} refusée(s), ${not_found.length} introuvable(s)`);
      setSelection(new Set());
      // reload
      setPage(0);
      setDebouncedSearch((s) => s);
      const params = { limit: PAGE_SIZE, skip: 0 };
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/invoices", { params });
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const runBulkMarkPaid = async () => {
    setBusy(true);
    try {
      const r = await api.post("/invoices/actions/bulk-mark-paid", { year: markPaidYear });
      toast.success(`${r.data.marked} facture(s) marquée(s) payée(s) pour ${markPaidYear}`);
      setShowMarkPaid(false);
      // reload
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/invoices", { params });
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {!embedded && (
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}>
              Factures émises
            </h1>
          )}
          <p className="text-slate-500 text-sm">
            <span data-testid="invoices-count">{total} facture{total > 1 ? "s" : ""}</span>
            {" · "}
            <span className="font-semibold text-slate-700" data-testid="invoices-total-ttc">
              {fmtCurrency(totalTtc)} TTC
            </span>
            {hasFilters && <span className="ml-1 text-blue-600">(filtré)</span>}
            {pageCount > 1 && ` — page ${page + 1}/${pageCount}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={exportExcel} variant="outline" size="sm" data-testid="export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Exporter Excel
          </Button>
          {canWrite && (
            <Button onClick={() => setShowMarkPaid(true)} variant="outline" size="sm"
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    data-testid="bulk-mark-paid-btn">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Clôturer année
            </Button>
          )}
          <Link to="/app/invoices/import">
            <Button size="sm" variant="outline" className="border-slate-200"
                     data-testid="invoice-import-btn">
              <FileText className="w-4 h-4 mr-1.5" /> Importer PDF
            </Button>
          </Link>
          <Link to="/app/invoices/import-batch">
            <Button size="sm" variant="outline" className="border-slate-200"
                     data-testid="invoice-import-batch-btn">
              <FileText className="w-4 h-4 mr-1.5" /> Import en masse
            </Button>
          </Link>
          <Link to="/app/invoices/history">
            <Button size="sm" variant="outline" className="border-slate-200"
                     data-testid="invoice-history-btn">
              <Download className="w-4 h-4 mr-1.5" /> Historique
            </Button>
          </Link>
          <Link to={canRebill ? "/app/invoices/rebilling" : "#"}
                onClick={(e) => {
                  if (!canRebill) {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent("clarix:upsell",
                      { detail: { feature: "rebilling" } }));
                  }
                }}>
            <Button size="sm" variant="outline"
                     className={cn(
                       "border-violet-300 text-violet-700 hover:bg-violet-50",
                       !canRebill && "opacity-70"
                     )}
                     data-testid="invoice-rebilling-btn">
              {canRebill
                ? <Sparkles className="w-4 h-4 mr-1.5" />
                : <Lock className="w-3.5 h-3.5 mr-1.5" />}
              Refacturer
              {!canRebill && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[9px] uppercase tracking-wider font-semibold">
                  Pro+
                </span>
              )}
            </Button>
          </Link>
          <Link to="/app/invoices/new">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="invoice-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Nouvelle facture
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200">
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-3">
            <div className="relative col-span-2 sm:flex-1 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input id="invoice-search" name="invoice-search" placeholder="Rechercher par N°, client..." value={search}
                     onChange={(e) => setSearch(e.target.value)}
                     className="pl-10" data-testid="invoice-search-input" />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger id="invoice-status-filter" name="invoice-status-filter" className="w-full sm:w-36" data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="draft">Brouillons</SelectItem>
                <SelectItem value="issued">Émises</SelectItem>
                <SelectItem value="paid">Payées</SelectItem>
                <SelectItem value="cancelled">Annulées</SelectItem>
              </SelectContent>
            </Select>
            <Select value={reconFilter} onValueChange={(v) => { setReconFilter(v); setPage(0); }}>
              <SelectTrigger id="invoice-recon-filter" name="invoice-recon-filter" className="w-full sm:w-44" data-testid="recon-filter">
                <SelectValue placeholder="Rapprochement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous rapprochements</SelectItem>
                <SelectItem value="matched">✅ Rapprochées</SelectItem>
                <SelectItem value="partial">🟡 Partielles</SelectItem>
                <SelectItem value="unmatched">❌ Non rapprochées</SelectItem>
              </SelectContent>
            </Select>
            <Select value={monthFilter} onValueChange={(v) => { setMonthFilter(v); setPage(0); }}>
              <SelectTrigger id="invoice-month-filter" name="invoice-month-filter" className="w-full sm:w-32" data-testid="month-filter">
                <SelectValue placeholder="Mois" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous mois</SelectItem>
                {MONTHS_FR.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={(v) => { setYearFilter(v); setPage(0); }}>
              <SelectTrigger id="invoice-year-filter" name="invoice-year-filter" className="w-full sm:w-28" data-testid="year-filter">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes années</SelectItem>
                {YEARS.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setShowAdvanced((v) => !v)}
                    className={cn("col-span-2 sm:col-span-1",
                      showAdvanced && "border-blue-300 bg-blue-50 text-blue-700")}
                    data-testid="advanced-toggle">
              <Filter className="w-4 h-4 mr-1" /> Plage & montant
            </Button>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}
                       className="text-slate-500 hover:text-slate-900 col-span-2 sm:col-span-1"
                       data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-1" /> Effacer
              </Button>
            )}
          </div>
          {showAdvanced && (
            <div className="flex flex-col gap-3 pt-2 border-t border-slate-100">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                <Label className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap sm:w-24">
                  📅 Période
                </Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="date-from" className="text-xs text-slate-500 whitespace-nowrap">Du</Label>
                  <Input id="date-from" name="date-from" type="date" value={dateFrom}
                         onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                         className="w-full sm:w-40" data-testid="date-from-input" />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="date-to" className="text-xs text-slate-500 whitespace-nowrap">Au</Label>
                  <Input id="date-to" name="date-to" type="date" value={dateTo}
                         onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                         className="w-full sm:w-40" data-testid="date-to-input" />
                </div>
                {dateFrom && !dateTo && (
                  <button type="button"
                          onClick={() => { setDateTo(dateFrom); setPage(0); }}
                          className="text-[11px] text-blue-600 hover:underline"
                          data-testid="date-exact-btn">
                    Filtrer sur cette date exacte
                  </button>
                )}
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                <Label className="text-xs uppercase tracking-wider text-slate-500 whitespace-nowrap sm:w-24">
                  💶 Montant TTC
                </Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="amount-min" className="text-xs text-slate-500 whitespace-nowrap">Min €</Label>
                  <Input id="amount-min" name="amount-min" type="number" step="0.01" min="0" value={amountMin}
                         onChange={(e) => { setAmountMin(e.target.value); setPage(0); }}
                         placeholder="0"
                         className="w-full sm:w-32 font-mono text-right" data-testid="amount-min-input" />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="amount-max" className="text-xs text-slate-500 whitespace-nowrap">Max €</Label>
                  <Input id="amount-max" name="amount-max" type="number" step="0.01" min="0" value={amountMax}
                         onChange={(e) => { setAmountMax(e.target.value); setPage(0); }}
                         placeholder="—"
                         className="w-full sm:w-32 font-mono text-right" data-testid="amount-max-input" />
                </div>
              </div>
              <p className="text-xs text-slate-400 italic">
                💡 La période exacte prend le pas sur les filtres Mois / Année. Astuce : sélectionne un mois <b>sans</b> l'année pour voir tous les mois de tous les ans (ex : tous les "février").
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Batch action bar */}
      {selection.size > 0 && canWrite && (
        <div className="sticky top-2 z-10 flex items-center justify-between px-4 py-2.5 bg-blue-600 text-white rounded-lg shadow-lg"
             data-testid="batch-action-bar">
          <div className="text-sm font-medium">
            {selection.size} brouillon{selection.size > 1 ? "s" : ""} sélectionné{selection.size > 1 ? "s" : ""}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelection(new Set())}
                    className="text-white hover:bg-blue-700">
              Désélectionner
            </Button>
            <Button size="sm" onClick={runBatchDelete} disabled={busy}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="batch-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 sm:p-6 space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-12 skeleton rounded"></div>
              ))}
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-16 text-center">
              <FileText className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">
                {hasFilters ? "Aucun résultat pour ces filtres." : "Aucune facture émise."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[700px]">
                <TableHeader>
                  <TableRow className="bg-slate-50/80 border-b border-slate-200">
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider">N° Facture</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider">Client</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider">Date</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider hidden md:table-cell">Échéance</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider text-right">Total HT</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider text-right hidden md:table-cell">TVA</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider text-right">Total TTC</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider">Statut</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider hidden lg:table-cell">Règlement</TableHead>
                    <TableHead className="text-xs uppercase text-slate-500 font-semibold tracking-wider">Source</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv, idx) => {
                    const num = inv.number || inv.invoice_number;
                    const total_ttc = inv.totals?.total_ttc ?? inv.total_ttc ?? 0;
                    const total_ht = inv.totals?.subtotal_ht ?? inv.total_ht ?? 0;
                    const total_vat = inv.totals?.total_vat ?? inv.total_vat ?? 0;
                    const source = inv.imported_from?.split(":")[0] || "native";
                    // Reconciliation bucket for the "Règlement" column (mirrors backend logic)
                    const paid_amt = Number(inv.paid_amount_ttc || 0);
                    const credited_amt = Number(inv.credited_amount_ttc || 0);
                    const remaining = Number(total_ttc) - paid_amt - credited_amt;
                    const isReconciled = inv.status === "paid" || !!inv.reconciled_transaction_id;
                    const isPartial = !isReconciled && paid_amt > 0.01 && remaining > 0.01;
                    const reconMeta = (inv.status === "draft" || inv.status === "cancelled")
                      ? { label: "—", cls: "bg-slate-50 text-slate-400 border-slate-200" }
                      : isReconciled
                        ? { label: "Rapprochée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
                        : isPartial
                          ? { label: "Partielle", cls: "bg-amber-50 text-amber-700 border-amber-200" }
                          : { label: "Non rapprochée", cls: "bg-red-50 text-red-700 border-red-200" };
                    return (
                      <TableRow key={inv.id}
                        className="cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100"
                        onClick={() => openDetail(inv)}
                        data-testid={`invoice-row-${idx}`}>
                        <TableCell className="w-10 pr-0" onClick={(e) => e.stopPropagation()}>
                          {inv.status === "draft" && canWrite ? (
                            <Checkbox
                              checked={selection.has(inv.id)}
                              onCheckedChange={(c) => toggleRow(inv.id, c)}
                              data-testid={`select-draft-${inv.id}`} />
                          ) : (
                            inv.file_asset_id && <Paperclip className="w-4 h-4 text-blue-500" />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-blue-600 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            {num || "—"}
                            {suggestions[inv.id] && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSuggestionDialog({ invoice: inv, suggestion: suggestions[inv.id] });
                                }}
                                title={`Suggestion : ${suggestions[inv.id].reason}`}
                                data-testid={`suggestion-badge-${idx}`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-100 hover:bg-blue-200 border border-blue-300 text-[10px] font-semibold text-blue-800 transition-colors">
                                <Sparkles className="w-3 h-3" />
                                Suggestion
                              </button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] text-sm">
                          <div className="truncate" title={inv.client?.name}>{inv.client?.name || "—"}</div>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap font-mono">{fmtDate(inv.issued_at || inv.invoice_date)}</TableCell>
                        <TableCell className="text-sm whitespace-nowrap hidden md:table-cell font-mono">{fmtDate(inv.due_date)}</TableCell>
                        <TableCell className="font-mono text-sm text-right whitespace-nowrap">{fmtCurrency(total_ht)}</TableCell>
                        <TableCell className="font-mono text-sm text-right text-slate-500 whitespace-nowrap hidden md:table-cell">{fmtCurrency(total_vat)}</TableCell>
                        <TableCell className="font-mono text-sm text-right font-semibold whitespace-nowrap text-slate-900">{fmtCurrency(total_ttc)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn("text-xs",
                            inv.status === "paid" ? "status-paid" :
                            inv.status === "issued" ? "status-facture" :
                            inv.status === "draft" ? "status-draft" :
                            inv.status === "cancelled" ? "status-inactive" : "")}>
                            {({paid:"Payée",issued:"Émise",draft:"Brouillon",cancelled:"Annulée",imported:"Importée",overdue:"En retard",partially_paid:"Part. payée"}[inv.status]) || inv.status || "—"}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <Badge variant="outline"
                                 className={cn("text-[10px]", reconMeta.cls)}
                                 data-testid={`recon-badge-${idx}`}>
                            {reconMeta.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn("text-[10px] uppercase",
                            source === "axonaut" ? "bg-orange-50 text-orange-700 border-orange-200" :
                            source === "pennylane" ? "bg-violet-50 text-violet-700 border-violet-200" :
                            source === "iopole" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                            "bg-blue-50 text-blue-700 border-blue-200")}>
                            {({axonaut:"Axonaut",pennylane:"Pennylane",iopole:"IOPOLE",native:"Manuel",manual:"Manuel"}[source]) || source}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Sheet — composant unifié partagé avec Clients.jsx */}
      <InvoiceDetailSheet
        invoiceId={selectedId}
        open={sheetOpen}
        onOpenChange={(v) => { setSheetOpen(v); if (!v) setSelectedId(null); }}
        onDeleted={(deletedId) => {
          setInvoices((prev) => prev.filter((i) => i.id !== deletedId));
          setTotal((n) => Math.max(0, n - 1));
        }}
      />

      {/* Suggestion reconciliation dialog — pastille 1-clic */}
      <Dialog open={!!suggestionDialog} onOpenChange={(v) => !v && setSuggestionDialog(null)}>
        <DialogContent data-testid="suggestion-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Suggestion de rapprochement
            </DialogTitle>
            <DialogDescription>
              Une transaction bancaire semble correspondre à cette facture.
              Validez en un clic pour la marquer payée.
            </DialogDescription>
          </DialogHeader>
          {suggestionDialog && (
            <div className="space-y-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Facture :</span>
                  <span className="font-mono font-semibold">
                    {suggestionDialog.invoice.number || suggestionDialog.invoice.invoice_number}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Client :</span>
                  <span>{suggestionDialog.invoice.client?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Montant TTC :</span>
                  <span className="font-mono font-semibold text-slate-800">
                    {fmtCurrency(suggestionDialog.invoice.totals?.total_ttc ?? suggestionDialog.invoice.total_ttc ?? 0)}
                  </span>
                </div>
              </div>
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-1 text-sm">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-700 mb-1">
                  ↔ Transaction bancaire correspondante
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Date :</span>
                  <span className="font-mono">{fmtDate(suggestionDialog.suggestion.tx_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Montant :</span>
                  <span className="font-mono font-semibold text-emerald-700">
                    {fmtCurrency(suggestionDialog.suggestion.tx_amount)}
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate mt-1"
                      title={suggestionDialog.suggestion.tx_description}>
                  {suggestionDialog.suggestion.tx_description}
                </div>
                <div className="pt-2 text-[11px] italic text-blue-700 border-t border-blue-200 mt-2">
                  💡 {suggestionDialog.suggestion.reason}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline"
                    onClick={() => setSuggestionDialog(null)}
                    disabled={reconciling}
                    data-testid="suggestion-cancel-btn">
              Ignorer
            </Button>
            <Button onClick={confirmReconciliation} disabled={reconciling}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="suggestion-confirm-btn">
              {reconciling
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Rapprochement…</>
                : <><CheckCircle2 className="w-4 h-4 mr-1" />Confirmer le rapprochement</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk mark-paid dialog */}
      <Dialog open={showMarkPaid} onOpenChange={setShowMarkPaid}>        <DialogContent data-testid="bulk-mark-paid-dialog">
          <DialogHeader><DialogTitle>Clôturer une année comptable</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Marque comme <b>payées</b> toutes les factures émises de l'année sélectionnée.
              Action idempotente — utile pour la clôture annuelle.
            </p>
            <div>
              <Label htmlFor="bulk-mark-paid-year" className="text-xs">Année à clôturer</Label>
              <Input id="bulk-mark-paid-year" name="bulk-mark-paid-year" type="number" min={2000} max={2100}
                     value={markPaidYear}
                     onChange={(e) => setMarkPaidYear(parseInt(e.target.value, 10) || 0)}
                     className="mt-1 font-mono"
                     data-testid="bulk-mark-paid-year" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarkPaid(false)}>Annuler</Button>
            <Button onClick={runBulkMarkPaid} disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="bulk-mark-paid-confirm">
              Clôturer {markPaidYear}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pagination — server-driven, keeps the DOM small even with 2000+ invoices */}
      {pageCount > 1 && (
        <div className="flex items-center justify-between px-1"
             data-testid="invoices-pagination">
          <p className="text-xs text-slate-500">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} sur {total}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={!canPrev}
                     onClick={() => setPage((p) => Math.max(0, p - 1))}
                     data-testid="pagination-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs font-mono text-slate-600 min-w-[3rem] text-center">
              {page + 1} / {pageCount}
            </span>
            <Button variant="outline" size="sm" disabled={!canNext}
                     onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                     data-testid="pagination-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
